import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PRODUCTS } from "@/data/site";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";

const CATEGORIES = ["All", ...Array.from(new Set(PRODUCTS.map((p) => p.category)))];

export default function Products() {
  const [cat, setCat] = useState("All");
  const items = useMemo(() => (cat === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat)), [cat]);

  return (
    <main data-testid="products-page" className="pt-24 md:pt-32 pb-24">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="chapter-index text-emerald-700">Loan Products</p>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-3 max-w-3xl">Fourteen loans. <span className="italic text-emerald-800">One partner.</span></h1>
        <p className="mt-6 text-slate-600 max-w-2xl">From new trucks to tractors, JCBs to home loans — every product is delivered with the same transparency and advisor-led service.</p>

        <div data-testid="product-filters" className="mt-10 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setCat(c)}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${cat === c ? 'bg-slate-900 text-white' : 'border border-slate-300 hover:border-slate-900'}`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((p) => (
            <motion.div key={p.slug} id={p.slug} layout initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:0.6, ease:[0.22,1,0.36,1]}}>
              <Link to="/apply" className="group block rounded-3xl overflow-hidden bg-white border border-black/[0.06] hover:shadow-2xl hover:shadow-emerald-900/10 transition-all">
                <div className="relative aspect-[5/4] overflow-hidden clip-frame">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]"/>
                  <div className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-widest">{p.category}</div>
                </div>
                <div className="p-6">
                  <p className="font-display text-2xl text-slate-900 group-hover:text-emerald-800 transition-colors">{p.name}</p>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  <p className="mt-4 text-sm inline-flex items-center gap-2 text-emerald-800">Apply <ArrowRight size={14}/></p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <Reveal>
          <div className="mt-20 rounded-3xl bg-slate-900 text-white p-10 md:p-14 grain relative overflow-hidden">
            <h2 className="font-display text-4xl md:text-6xl leading-tight max-w-2xl">Not sure which loan fits? <span className="italic text-emerald-400">Talk to a human.</span></h2>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/apply" className="btn-line inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-6 py-3 text-sm font-medium">Apply for a Loan <ArrowRight size={16}/></Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:border-white text-white px-6 py-3 text-sm">Request a Callback</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
